import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';

/**
 * Lookup-Liste der unterstützten Service-Stacks (GET /service-types).
 *
 * Das Backend liefert `{ data: ServiceTypeItem[], total }` mit den Feldern
 * `code / display_name / category`. Für bestehende Konsumenten (Drift-,
 * System-Filter) mappen wir zusätzlich die früheren Alias-Felder
 * `slug / name / family` mit — so bleibt deren Code unverändert.
 *
 * Ändert sich praktisch nie während einer Session -> langer staleTime.
 */
export interface ServiceTypeOption {
  id: string;
  code: string;
  display_name: string;
  category: string;
  description: string | null;
  eol_date: string | null;
  is_active: boolean;
  // Rückwärts-kompatible Aliase (== code / display_name / category):
  slug: string;
  name: string;
  family: string;
}

interface ServiceTypeWire {
  id: string;
  code: string;
  display_name: string;
  category: string;
  description: string | null;
  eol_date: string | null;
  is_active: boolean;
}

interface ServiceTypeListResponse {
  data: ServiceTypeWire[];
  total: number;
}

export function useServiceTypes(): ReturnType<typeof useQuery<ServiceTypeOption[]>> {
  return useQuery<ServiceTypeOption[]>({
    queryKey: queryKeys.serviceTypes.all,
    queryFn: async () => {
      const res = await api.get<ServiceTypeListResponse>('/service-types');
      const rows = Array.isArray(res?.data) ? res.data : [];
      return rows.map((r) => ({
        ...r,
        slug: r.code,
        name: r.display_name,
        family: r.category,
      }));
    },
    staleTime: 15 * 60 * 1000,
  });
}
